/**
 * Showing a promotion in the cart, before Stripe has said anything.
 *
 * This is an ESTIMATE and the code says so everywhere, because the authority is the Checkout Session and
 * the order is reconciled from it after payment (`checkoutTotalsFromSession`). What is computed here only
 * has to be right enough that the customer can decide to pay — and, more importantly, must never be
 * *higher* than what Stripe will actually take off, so the total shown is never lower than the total
 * charged. A cart that promises more than the card is charged is the one failure mode worth designing out.
 *
 * So the discount is applied to the ITEMS subtotal only, never to shipping: Stripe applies a coupon to
 * eligible line items, and the shipping rate is a separate `shipping_options` entry it does not discount.
 * Including shipping would overstate a percentage discount by the shipping cost.
 */

export interface AppliedPromo {
  valid: boolean;
  reason?: string;
  code?: string;
  promotionCodeId?: string;
  label?: string;
  percentOff?: number;
  amountOffCents?: number;
  minimumAmountCents?: number;
}

/**
 * What the promotion takes off the items subtotal, in cents.
 *
 * Rounded DOWN (`Math.floor`), so a half-cent never becomes a cent we promised and Stripe did not give.
 * Never more than the subtotal — a $50-off coupon on a $30 order is a free order, not a $20 credit.
 */
export function estimateDiscountCents(promo: AppliedPromo | null, subtotalCents: number): number {
  if (!promo?.valid || subtotalCents <= 0) return 0;
  // Below the minimum, Stripe refuses the code outright — showing a reduction here would be a lie.
  if (promo.minimumAmountCents != null && subtotalCents < promo.minimumAmountCents) return 0;
  if (promo.percentOff) return Math.min(subtotalCents, Math.floor((subtotalCents * promo.percentOff) / 100));
  if (promo.amountOffCents) return Math.min(subtotalCents, promo.amountOffCents);
  return 0;
}

/**
 * Why an otherwise-valid code is taking nothing off, or null when there is nothing to explain.
 *
 * A code that applies but shows no reduction reads as broken. The minimum-order case is the one that
 * actually happens, and it is worth saying because the customer can act on it.
 */
export function promoBlockedReason(promo: AppliedPromo | null, subtotalCents: number): string | null {
  if (!promo?.valid) return null;
  const min = promo.minimumAmountCents;
  if (min != null && subtotalCents < min) {
    return `Applies to orders over $${(min / 100).toFixed(2)}.`;
  }
  return null;
}
